import axios from 'axios';
import { v4 as uuidv4 } from "uuid";
import { redirectExpiredSession } from "../auth-redirect";
import { WORDPRESS_INTEGRATION_PATH } from "./wordpress-path";

export { WORDPRESS_INTEGRATION_PATH } from "./wordpress-path";

//export const development_mode = false;
export const node_environment = process.env.REACT_APP_NODE_ENV;
export const development_mode = (node_environment === 'development');//(!process.env.NODE_ENV || process.env.NODE_ENV === 'development');
export const login_development_mode = !development_mode;

const api_server_secure = !development_mode;
const api_server_address = development_mode ? "localhost" : "api.survey.feradel.com";
const api_server_port = development_mode ? 11600 : 443;
const api_server_path = "api";

export function url() {
         // http[s]://server:port/path
  return `http${api_server_secure ? "s" : ""}://${api_server_address}:${api_server_port}/${api_server_path}`;
}

const api = axios.create({
  baseURL: url(),
  headers: {
    'Content-Type': 'application/json'
  }
});
let redirectingToLogin = false;

api.interceptors.request.use(
  cfg => {
    if (localStorage.token) cfg.headers['x-auth-token'] = localStorage.token;
    if (localStorage.instance) cfg.headers['instance'] = localStorage.instance;
    if (!cfg.headers['x-correlation-id']) cfg.headers['x-correlation-id'] = uuidv4();
    return cfg;
  },
  err => {
    return Promise.reject(err);
  }
);

api.interceptors.response.use(
  res => res,
  err => {
    if (!err?.config?.skipAuthRedirect
      && !redirectingToLogin
      && redirectExpiredSession(err)) {
      redirectingToLogin = true;
    }
    return Promise.reject(err);
  }
);

export default api;

export const messageFromError = (error, fallback = "The request could not be completed.") =>
  error?.response?.data?.error?.message
  || error?.response?.data?.message
  || error?.response?.data?.msg
  || error?.message
  || fallback;

export const runtimeSettings = async () => {
  const response = await api.get("/utility/runtime");
  return response.data;
};

export const currentUsage = async () => {
  const response = await api.get("/usage/current");
  return response.data;
};

export const usageHistory = async () => {
  const response = await api.get("/usage/history");
  return response.data;
};

export const listSurveys = async (parameters = {}) => {
  const response = await api.get("/surveys", { params: parameters });
  return response.data;
};

export const getSurvey = async (surveyId) => {
  const response = await api.get(`/surveys/${encodeURIComponent(surveyId)}`);
  return response.data;
};

export const getSurveyProperties = async (surveyId) => {
  const response = await api.get(`/surveys/${encodeURIComponent(surveyId)}/properties`);
  return response.data;
};

export const createSurvey = async (values) => {
  const response = await api.post("/surveys", values);
  return response.data;
};

export const updateSurvey = async (surveyId, values) => {
  const response = await api.patch(`/surveys/${encodeURIComponent(surveyId)}`, values);
  return response.data;
};

export const runSurveyAction = async (surveyId, action) => {
  const id = encodeURIComponent(surveyId);
  const response = action === "delete"
    ? await api.delete(`/surveys/${id}`)
    : await api.post(`/surveys/${id}/${encodeURIComponent(action)}`, {});
  return response.data;
};

export const getSurveyBuilder = async (surveyId) => {
  const response = await api.get(`/surveys/${encodeURIComponent(surveyId)}/builder`);
  return response.data;
};

export const saveSurveyBuilder = async (surveyId, values) => {
  const response = await api.put(
    `/surveys/${encodeURIComponent(surveyId)}/builder`,
    values
  );
  return response.data;
};

export const listSurveyPublicLinks = async (surveyId) => {
  const response = await api.get(
    `/surveys/${encodeURIComponent(surveyId)}/public-links`
  );
  return response.data;
};

export const createSurveyPublicLink = async (surveyId, values) => {
  const response = await api.post(
    `/surveys/${encodeURIComponent(surveyId)}/public-links`,
    values
  );
  return response.data;
};

export const updateSurveyPublicLink = async (surveyId, linkId, values) => {
  const response = await api.patch(
    `/surveys/${encodeURIComponent(surveyId)}/public-links/${encodeURIComponent(linkId)}`,
    values
  );
  return response.data;
};

export const rotateSurveyPublicLink = async (surveyId, linkId) => {
  const response = await api.post(
    `/surveys/${encodeURIComponent(surveyId)}/public-links/${encodeURIComponent(linkId)}/rotate`,
    {}
  );
  return response.data;
};

export const disableSurveyPublicLink = async (surveyId, linkId) => {
  const response = await api.delete(
    `/surveys/${encodeURIComponent(surveyId)}/public-links/${encodeURIComponent(linkId)}`
  );
  return response.data;
};

export const getGenericSurvey = async (publicKey, parameters = {}) => {
  const response = await api.get(
    `/public/links/${encodeURIComponent(publicKey)}`,
    { params: parameters }
  );
  return response.data;
};

const publicResponseHeaders = (resumeToken, idempotencyKey) => ({
  ...(resumeToken ? { "x-survey-resume-token": resumeToken } : {}),
  ...(idempotencyKey ? { "idempotency-key": idempotencyKey } : {}),
});

export const resolveGenericSurveyResponse = async (
  publicKey,
  values = {},
  resumeToken = ""
) => {
  const response = await api.post(
    `/public/links/${encodeURIComponent(publicKey)}/responses/resolve`,
    values,
    { headers: publicResponseHeaders(resumeToken) }
  );
  return response.data;
};

export const saveGenericSurveyResponse = async (
  publicKey,
  responseId,
  values,
  resumeToken,
  idempotencyKey
) => {
  const response = await api.post(
    `/public/links/${encodeURIComponent(publicKey)}/responses/${encodeURIComponent(responseId)}/save`,
    values,
    { headers: publicResponseHeaders(resumeToken, idempotencyKey) }
  );
  return response.data;
};

export const submitGenericSurveyResponse = async (
  publicKey,
  responseId,
  values,
  resumeToken,
  idempotencyKey
) => {
  const response = await api.post(
    `/public/links/${encodeURIComponent(publicKey)}/responses/${encodeURIComponent(responseId)}/submit`,
    values,
    { headers: publicResponseHeaders(resumeToken, idempotencyKey) }
  );
  return response.data;
};

export const listResults = async (parameters = {}, surveyId = null) => {
  const path = surveyId
    ? `/surveys/${encodeURIComponent(surveyId)}/results`
    : "/results";
  const response = await api.get(path, { params: parameters });
  return response.data;
};

export const getResultReport = async (surveyId, responseId, version = null) => {
  const response = await api.get(
    `/surveys/${encodeURIComponent(surveyId)}/results/${encodeURIComponent(responseId)}`,
    { params: version ? { version } : {} }
  );
  return response.data;
};

export const getAggregateReport = async (surveyId, parameters = {}) => {
  const response = await api.get(
    `/surveys/${encodeURIComponent(surveyId)}/report`,
    { params: parameters }
  );
  return response.data;
};

export const updateResult = async (surveyId, responseId, values) => {
  const response = await api.patch(
    `/surveys/${encodeURIComponent(surveyId)}/results/${encodeURIComponent(responseId)}`,
    values
  );
  return response.data;
};

export const runResultAction = async (surveyId, responseId, action) => {
  const path =
    `/surveys/${encodeURIComponent(surveyId)}/results/${encodeURIComponent(responseId)}`;
  const response = action === "delete"
    ? await api.delete(path)
    : await api.post(`${path}/${encodeURIComponent(action)}`, {});
  return response.data;
};

export const listInvitationCampaigns = async (parameters = {}) => {
  const response = await api.get("/invitations", { params: parameters });
  return response.data;
};

export const getInvitationCampaign = async campaignId => {
  const response = await api.get(`/invitations/${encodeURIComponent(campaignId)}`);
  return response.data;
};

export const previewInvitations = async values => {
  const response = await api.post("/invitations/preview", values);
  return response.data;
};

export const sendInvitations = async (values, idempotencyKey) => {
  const response = await api.post("/invitations", values, {
    headers: { "idempotency-key": idempotencyKey },
  });
  return response.data;
};

export const remindInvitation = async invitationId => {
  const response = await api.post(
    `/invitations/recipients/${encodeURIComponent(invitationId)}/remind`,
    {}
  );
  return response.data;
};

export const resendInvitation = async invitationId => {
  const response = await api.post(
    `/invitations/recipients/${encodeURIComponent(invitationId)}/resend`,
    {}
  );
  return response.data;
};

export const cancelInvitationCampaign = async campaignId => {
  const response = await api.post(
    `/invitations/${encodeURIComponent(campaignId)}/cancel`,
    {}
  );
  return response.data;
};

export const createSurveyExport = async (surveyId, values) => {
  const response = await api.post(
    `/surveys/${encodeURIComponent(surveyId)}/exports`,
    values
  );
  return response.data;
};

export const listSurveyExports = async surveyId => {
  const response = await api.get(`/surveys/${encodeURIComponent(surveyId)}/exports`);
  return response.data;
};

export const getSurveyExport = async (surveyId, exportId) => {
  const response = await api.get(
    `/surveys/${encodeURIComponent(surveyId)}/exports/${encodeURIComponent(exportId)}`
  );
  return response.data;
};

export const cancelSurveyExport = async (surveyId, exportId) => {
  const response = await api.post(
    `/surveys/${encodeURIComponent(surveyId)}/exports/${encodeURIComponent(exportId)}/cancel`,
    {}
  );
  return response.data;
};

export const downloadSurveyExport = async download => {
  const response = await api.get(download.url, { responseType: "blob" });
  return response.data;
};

export const listSurveyWebhooks = async () => {
  const response = await api.get("/webhooks");
  return response.data;
};

export const createSurveyWebhook = async values => {
  const response = await api.post("/webhooks", values);
  return response.data;
};

export const updateSurveyWebhook = async (webhookId, values) => {
  const response = await api.patch(`/webhooks/${encodeURIComponent(webhookId)}`, values);
  return response.data;
};

export const deleteSurveyWebhook = async webhookId => {
  const response = await api.delete(`/webhooks/${encodeURIComponent(webhookId)}`);
  return response.data;
};

export const testSurveyWebhook = async webhookId => {
  const response = await api.post(`/webhooks/${encodeURIComponent(webhookId)}/test`, {});
  return response.data;
};

export const rotateSurveyWebhookSecret = async webhookId => {
  const response = await api.post(
    `/webhooks/${encodeURIComponent(webhookId)}/rotate-secret`,
    {}
  );
  return response.data;
};

export const listSurveyWebhookDeliveries = async webhookId => {
  const response = await api.get(
    `/webhooks/${encodeURIComponent(webhookId)}/deliveries`
  );
  return response.data;
};

export const replaySurveyWebhookDelivery = async (webhookId, deliveryId) => {
  const response = await api.post(
    `/webhooks/${encodeURIComponent(webhookId)}/deliveries/${encodeURIComponent(deliveryId)}/replay`,
    {}
  );
  return response.data;
};

export const surveyOperations = async () => {
  const response = await api.get("/operations");
  return response.data;
};

export const authorizeWordPressInstallation = async values => {
  const response = await api.post(
    `${WORDPRESS_INTEGRATION_PATH}/authorizations`,
    values
  );
  return response.data;
};

export const approveManualWordPressInstallation = async userCode => {
  const response = await api.post(
    `${WORDPRESS_INTEGRATION_PATH}/manual/approve`,
    { userCode }
  );
  return response.data;
};

export const wordpressInstallations = async () => {
  const response = await api.get(`${WORDPRESS_INTEGRATION_PATH}/installations`);
  return response.data;
};

export const requestWordPressInstallationRotation = async publicId => {
  const response = await api.post(
    `${WORDPRESS_INTEGRATION_PATH}/installations/${encodeURIComponent(publicId)}/rotation-request`,
    {}
  );
  return response.data;
};

export const revokeWordPressInstallation = async publicId => {
  const response = await api.post(
    `${WORDPRESS_INTEGRATION_PATH}/installations/${encodeURIComponent(publicId)}/revoke`,
    {}
  );
  return response.data;
};
